import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { formatDate } from 'src/utils/date';
import { beautifyEnum } from 'src/utils/utility';
import { MatchingServiceDto } from 'src/generated/services/TFinancialApi';
import { NoContractForm } from './types';

const useColumns = (): MRT_ColumnDef<MatchingServiceDto>[] => {
  return [
    {
      enableEditing: false,
      accessorKey: 'polpod',
      header: 'Trade Lane',
      accessorFn: (row) => {
        return <span>{row?.listZone?.map((item) => `${item?.pol} / ${item?.pod}`)?.join(', ')}</span>;
      },
    },
    {
      enableEditing: false,
      accessorKey: 'shipperName',
      header: 'Shipper Name',
      accessorFn: (row) => <span>{row?.shipperName ? beautifyEnum(row.shipperName).toUpperCase() : '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'consigneeName',
      header: 'Consignee Name',
      accessorFn: (row) => <span>{row?.consigneeName ? beautifyEnum(row.consigneeName).toUpperCase() : '-'}</span>,
    },
    {
      header: 'Billed Entity',
      enableEditing: false,
      accessorKey: 'billingPartyCode',
      accessorFn: (row) => <span>{row?.billingPartyCode ? beautifyEnum(row.billingPartyCode).toUpperCase() : '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'service',
      header: 'Service',
      accessorFn: (row) => <span>{row?.service || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'type',
      header: 'Type',
      accessorFn: (row) => <span>{row?.type || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      header: 'Modality',
      accessorFn: (row) => <span>{row?.modality || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'carrier',
      header: 'Carrier',
      accessorFn: (row) => <span>{row?.carrier || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'incoterm',
      header: 'Incoterm',
      accessorFn: (row) => <span>{row?.incoterm || '-'}</span>,
    },
    {
      header: 'Validity Period',
      id: 'validityPeriod',
      accessorFn: () => '-', // Placeholder, will be overridden in the table instance
      enableEditing: false,
    },
  ];
};

interface ITableSkipProps {
  data: MatchingServiceDto[];
}

export const TableSkip: FC<ITableSkipProps> = (props) => {
  const { data = [] } = props;
  const { watch } = useFormContext<NoContractForm>();
  const startDate = watch('newMatchingService.existingShippingService.validityPeriod.start');
  const endDate = watch('newMatchingService.existingShippingService.validityPeriod.end');

  const columns = useColumns();

  // Override the Validity Period column to show form data
  const columnsWithValidityPeriod = columns.map((col) => {
    if (col.id === 'validityPeriod') {
      return {
        ...col,
        accessorFn: () => {
          if (startDate && endDate) {
            return (
              <span>
                {formatDate(new Date(startDate))} - {formatDate(new Date(endDate))}
              </span>
            );
          }
          return '-';
        },
      };
    }
    return col;
  });

  const table = useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    enableRowSelection: false,
    enableMultiRowSelection: false,
    data,
    columns: columnsWithValidityPeriod,
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
  });

  return <CustomTable table={table} />;
};
